import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["sa", "sb", "spaceAbove", "spaceBelow"],
    _excluded2 = ["textStyle", "theme"],
    _excluded3 = ["theme"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * Space logic, providing the spaceAbove (sa) and spaceBelow (sb) props
 */
import PropTypes from 'prop-types';
import { createMediaQuery } from 'styled-system';
import { makeScaledArrayStyles, makeScaledObjStyles } from '../scales';
import { styleFromVariant } from '../theme';
import { coerceFromString, mapObj, mergeAll } from '../utils';

var hasMtProp = function hasMtProp(_ref) {
  var marginTop = _ref.marginTop,
      mt = _ref.mt,
      my = _ref.my,
      m = _ref.m,
      margin = _ref.margin;
  return marginTop || mt || my || m || margin;
};

var hasMbProp = function hasMbProp(_ref2) {
  var marginBottom = _ref2.marginBottom,
      mb = _ref2.mb,
      my = _ref2.my,
      m = _ref2.m,
      margin = _ref2.margin;
  return marginBottom || mb || my || m || margin;
};

export var getSpacerKeys = function getSpacerKeys(_ref3) {
  var sa = _ref3.sa,
      sb = _ref3.sb,
      spaceAbove = _ref3.spaceAbove,
      spaceBelow = _ref3.spaceBelow,
      props = _objectWithoutProperties(_ref3, _excluded);

  return {
    spaceAbove: hasMtProp(props) ? null : coerceFromString(sa || spaceAbove),
    spaceBelow: hasMbProp(props) ? null : coerceFromString(sb || spaceBelow)
  };
};
/**
 * Get the minimal line height for a text style, the cap height
 */

export var capHeightForTextStyle = function capHeightForTextStyle(theme, textStyle) {
  return theme.lineHeights[textStyle] - theme.lineSpacings[textStyle].top - theme.lineSpacings[textStyle].bottom;
}; // TODO: dynamically calculate from aliases and array

export var breakpointOrder = function breakpointOrder(theme) {
  return ['default', 'sm', 'md', 'lg', 'xl'];
};
/**
 * Given a target breakpoint name, e.g. "lg", walk backwards through the
 * possible breakpoint aliases until a breakpoint is found in the styles
 * object of format { default: 'foo', md: 'bar', lg: 'baz' }.
 * @param {Theme} theme - for calculating the order of breakpoint aliases
 * @param {*} targetBreakpoint
 * @param {*} stylesObj
 */

export var findClosestBp = function findClosestBp(theme, targetBreakpoint, stylesObj) {
  var bpsReversed = breakpointOrder(theme).reverse();
  var startIndex = bpsReversed.indexOf(targetBreakpoint);

  for (var i = startIndex; i < bpsReversed.length; i++) {
    if (bpsReversed[i] in stylesObj) {
      return bpsReversed[i];
    }
  }

  return bpsReversed[bpsReversed.length - 1];
};
export var makeSpaceStyle = function makeSpaceStyle(_ref4) {
  var theme = _ref4.theme,
      spaceVals = _ref4.spaceVals,
      cssProp = _ref4.cssProp;
  return Array.isArray(spaceVals) ? makeScaledArrayStyles({
    theme: theme,
    styleObjArr: spaceVals.map(function (val) {
      return _defineProperty({}, cssProp, theme.space[val] || 0);
    })
  }) : makeScaledObjStyles({
    theme: theme,
    styleObjMap: mapObj(function (val) {
      return _defineProperty({}, cssProp, theme.space[val] || 0);
    }, spaceVals)
  });
};
export var marginsForStyle = function marginsForStyle(_ref7) {
  var theme = _ref7.theme,
      styleName = _ref7.styleName,
      spacerKeys = _ref7.spacerKeys;
  return {
    marginTop: (theme.space[spacerKeys.spaceAbove] || 0) - theme.lineSpacings[styleName].top,
    marginBottom: (theme.space[spacerKeys.spaceBelow] || 0) - theme.lineSpacings[styleName].bottom
  };
};
export var spaceValForBreakpoint = function spaceValForBreakpoint(_ref8) {
  var breakpointKey = _ref8.breakpointKey,
      theme = _ref8.theme,
      textStylesObj = _ref8.textStylesObj,
      spacesObj = _ref8.spacesObj,
      lineSpacingsKey = _ref8.lineSpacingsKey;

  try {
    return (theme.space[spacesObj[findClosestBp(theme, breakpointKey, spacesObj)]] || 0) - (theme.lineSpacings[textStylesObj[findClosestBp(theme, breakpointKey, textStylesObj)]][lineSpacingsKey] || 0);
  } catch (err) {
    return null;
  }
};
export var minTextMargin = function minTextMargin(_ref9) {
  var textStyles = _ref9.textStyles,
      theme = _ref9.theme,
      _ref9$spaces = _ref9.spaces,
      spaces = _ref9$spaces === void 0 ? {} : _ref9$spaces,
      cssProp = _ref9.cssProp,
      lineSpacingsKey = _ref9.lineSpacingsKey;
  var breakpoints = breakpointOrder(theme);

  if (!('default' in textStyles)) {
    console.warn('No textStyle prop provided');
    return {};
  }

  return breakpoints.reduce(function (acc, breakpointKey, i) {
    if (breakpointKey in spaces || breakpointKey in textStyles) {
      var cssVal = spaceValForBreakpoint({
        breakpointKey: breakpointKey,
        theme: theme,
        textStylesObj: textStyles,
        spacesObj: spaces,
        lineSpacingsKey: lineSpacingsKey
      });

      if (cssVal === null) {
        var err = new Error("Invalid text styles");
        err.textStyles = textStyles;
        throw err;
      }

      return i === 0 ? _objectSpread(_objectSpread({}, acc), {}, _defineProperty({}, cssProp, cssVal)) : _objectSpread(_objectSpread({}, acc), {}, _defineProperty({}, createMediaQuery(theme.breakpoints[breakpointKey]), _defineProperty({}, cssProp, cssVal)));
    }

    return acc;
  }, {});
};
export var scaledArrayToObj = function scaledArrayToObj(theme, scaledArray) {
  var breakpoints = breakpointOrder(theme);
  return Array.isArray(scaledArray) ? scaledArray.reduce(function (acc, val, i) {
    return breakpoints[i] ? _objectSpread(_objectSpread({}, acc), {}, _defineProperty({}, breakpoints[i], val)) : acc;
  }, {}) : scaledArray;
};
/**
 * Set the text margins to the requested spacing size minus the pixel distance
 * between the line height and the cap height of the text style. For no spacing
 * this means a negative margin, to compensate for the line height of the text
 * style; for spacing of, say, 30, this means the margin would be set to 26, to
 * account for the 4px distance between the line height and the cap height.
 *
 * This allows us to better achieve pixel perfection when aligning text to the
 * AMP design 5 pixel grid, which uses the top and bottom most pixels on the
 * capital letters of the font as the offset point for the grid - not the bottom
 * of the line height, as css is more naturally set up for.
 *
 * @param {string} props.textStyle - a valid textStyle key from the textStyle enum
 * @param {object} props.theme - the theme object that should by default be available
 * @param {string?} props.spaceAbove - a valid spacer key from the spacer enum
 * @param {string?} props.spaceBelow - a valid spacer key from the spacer enum
 * @param {string?} props.sa - an alias for spaceAbove
 * @param {string?} props.sb - an alias for spaceBelow
 * @returns {StyleObject[]}
 */

export var minTextMargins = function minTextMargins(_ref10) {
  var textStyle = _ref10.textStyle,
      theme = _ref10.theme,
      props = _objectWithoutProperties(_ref10, _excluded2);

  var _getSpacerKeys = getSpacerKeys(props),
      spaceAbove = _getSpacerKeys.spaceAbove,
      spaceBelow = _getSpacerKeys.spaceBelow;

  var textStyles = mapObj(styleFromVariant, scaledArrayToObj(theme, typeof textStyle === 'string' ? [textStyle] : textStyle));

  try {
    var top = spaceAbove === null ? {} : minTextMargin({
      textStyles: textStyles,
      theme: theme,
      spaces: scaledArrayToObj(theme, spaceAbove),
      cssProp: 'marginTop',
      lineSpacingsKey: 'top'
    });
    var bottom = spaceBelow === null ? {} : minTextMargin({
      textStyles: textStyles,
      theme: theme,
      spaces: scaledArrayToObj(theme, spaceBelow),
      cssProp: 'marginBottom',
      lineSpacingsKey: 'bottom'
    });
    return mergeAll(top, bottom);
  } catch (err) {
    if (err.textStyles) {
      console.warn("Unknown/empty textStyle prop ".concat(JSON.stringify(err.textStyles)));
      return {};
    }
  }
};
minTextMargins.propTypes = {};
/**
 * An alternative strategy for text grid styling where single lines of text sit aside
 * inline box elements. Here the line height is cut right down to the text caps.
 *
 * Annoyingly, setting the line height to 1em doesn't work consistently across browsers
 * for all text styles, so instead we calculate it dynamically from the text style properties
 * in the theme
 * @param {string} props.textStyle
 * @param {object} props.theme
 * @returns {StyleObject}
 */

export var minLineHeight = function minLineHeight(_ref11) {
  var textStyle = _ref11.textStyle,
      theme = _ref11.theme;
  return {
    lineHeight: capHeightForTextStyle(theme, styleFromVariant(textStyle))
  };
};
minLineHeight.propTypes = {};
export var boxSpacer = function boxSpacer(_ref12) {
  var theme = _ref12.theme,
      props = _objectWithoutProperties(_ref12, _excluded3);

  var _getSpacerKeys2 = getSpacerKeys(props),
      spaceAbove = _getSpacerKeys2.spaceAbove,
      spaceBelow = _getSpacerKeys2.spaceBelow;

  var above = spaceAbove ? makeSpaceStyle({
    theme: theme,
    spaceVals: spaceAbove,
    cssProp: 'marginTop'
  }) : [];
  var below = spaceBelow ? makeSpaceStyle({
    theme: theme,
    spaceVals: spaceBelow,
    cssProp: 'marginBottom'
  }) : [];
  return mergeAll.apply(void 0, [].concat(_toConsumableArray(above), _toConsumableArray(below)));
};
boxSpacer.propTypes = {
  sa: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  sb: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  spaceAbove: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  spaceBelow: PropTypes.oneOfType([PropTypes.string, PropTypes.array])
};
/**
 * Transform output from boxSpacer and minTextMargins to apply to
 * padding instead of margin, useful for links and hover areas.
 *
 * Warning: negative padding isn't valid, so padding must be greater
 * than 8px to space all text styles correctly, or greater than 4px
 * to space body styles correctly.
 */

export var marginToPadding = function marginToPadding(rules) {
  var out = {};

  for (var key in rules) {
    if (!(typeof rules[key] === 'number')) {
      out[key] = marginToPadding(rules[key]);
    } else if (key.startsWith('margin')) {
      out["padding".concat(key.slice(6))] = rules[key] < 0 ? 0 : rules[key];
    } else {
      out[key] = rules[key];
    }
  }

  return out;
};